/**
 * Vitals (Screen H) — a READABLE, member-keyed vital-sign view over op_fact_observation (REAL). Because
 * op_* carries NO human-readable LOINC name (op_dim_code is ABSENT; D-32), the vital TYPE is inferred
 * ONLY from the real columns via the pure deriveVitalType helper — the opaque code_key surrogate is
 * de-emphasized, never led with. The screen:
 *   1. groups observations by code_key (each distinct code_key = a distinct measure) and labels each by
 *      its inferred type;
 *   2. leads with LATEST-VALUE tiles (one per type) + per-type TREND sparklines (BP = dual series);
 *   3. lists free-text/value_string rows under an honest "Unstructured / free-text (DEFERRED until
 *      coded)" section — never trended;
 *   4. keeps a reshaped table (Vital type FIRST, surrogate demoted to a tooltip) + the ParticipantPicker
 *      defaulting to the vitals exemplar.
 * Every surface is badged from its row/section provenance (REAL / partial / unstructured), never
 * hand-tagged. Vital rows are never console.logged; an empty result shows the provenance badge, never a
 * silent zero.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyText,
  SkyTooltip,
  SkyLoader,
  SkyMessageBar,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { Vital, Provenance, DataStatus, ProvenanceFeed } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { vitalListSchema } from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { deriveVitalType, type VitalTypeInfo } from "../lib/vitalType";
import { vitalAdvisory, VITAL_ADVISORY_LABEL, VITAL_ADVISORY_LEGEND } from "../lib/vitalAdvisory";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { VitalSparkline, type SparkSeries } from "../components/VitalSparkline";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

// One distinct code_key = one measure. The group is labelled by the REAL gold LOINC name (code_display)
// when present; deriveVitalType is kept ONLY as the fallback (and always for the trend/format KIND).
interface VitalGroup {
  codeKey: string;
  info: VitalTypeInfo; // KIND (bp / numeric / unstructured) drives formatting + trending — always inferred
  displayName: string; // PRIMARY label: gold code_display, else the inferred type label
  hasGoldName: boolean; // true when the real LOINC name resolved (drop the surrogate from the headline)
  rows: Vital[]; // most-recent first (server orders effective_date_key DESC)
  latest: Vital;
}

/** A per-row Provenance so each tile/row badges from its OWN op_* columns (never the page roll-up). */
function rowProvenance(row: Vital): Provenance {
  return {
    dataStatus: (row.data_status as DataStatus) ?? "real",
    provenanceFeed: (row.provenance_feed as ProvenanceFeed) ?? "fhir",
    gapReason: row.gap_reason ?? null,
    unblockingFeed: null,
    asOf: null,
    populationCaveat: false,
    rowCount: 1,
  };
}

/** Group rows by code_key, preserving the server's date-DESC order so rows[0] is the latest. */
function groupVitals(rows: Vital[]): VitalGroup[] {
  const byKey = new Map<string, Vital[]>();
  for (const row of rows) {
    const list = byKey.get(row.code_key);
    if (list) list.push(row);
    else byKey.set(row.code_key, [row]);
  }
  return Array.from(byKey.entries()).map(([codeKey, groupRows]) => {
    const latest = groupRows[0];
    const info = deriveVitalType(latest);
    const goldName = latest.code_display?.trim() || null;
    return {
      codeKey,
      rows: groupRows,
      latest,
      info,
      displayName: goldName ?? info.label,
      hasGoldName: goldName !== null,
    };
  });
}

/** The headline value for a tile/row: BP as sys/dia mmHg, free-text verbatim, else value+unit. */
function formatLatest(row: Vital, info: VitalTypeInfo): string {
  if (info.kind === "bp" && row.component_systolic != null && row.component_diastolic != null) {
    return `${row.component_systolic}/${row.component_diastolic} mmHg`;
  }
  if (info.kind === "unstructured") return row.value_string?.trim() || "Free-text (no value)";
  if (row.value_quantity != null) {
    return `${row.value_quantity}${row.value_unit ? ` ${row.value_unit}` : ""}`;
  }
  return "Not recorded";
}

/** Build trend series (chronological) for a numeric group; null when < 2 points. Free-text never trends. */
function buildSeries(group: VitalGroup): SparkSeries[] | null {
  if (group.info.kind === "unstructured") return null;
  const chrono = [...group.rows].reverse(); // oldest -> newest
  if (group.info.kind === "bp") {
    const sys = chrono.filter((r) => r.component_systolic != null).map((r) => r.component_systolic!);
    const dia = chrono.filter((r) => r.component_diastolic != null).map((r) => r.component_diastolic!);
    const series: SparkSeries[] = [];
    if (sys.length >= 2) series.push({ values: sys, colorVar: "var(--color-blue-500)", label: "Systolic" });
    if (dia.length >= 2) series.push({ values: dia, colorVar: "var(--color-green-500)", label: "Diastolic" });
    return series.length ? series : null;
  }
  const vals = chrono.filter((r) => r.value_quantity != null).map((r) => r.value_quantity!);
  if (vals.length < 2) return null;
  return [{ values: vals, colorVar: "var(--color-brand-80)", label: group.info.label }];
}

/** A demoted, honest rendering of the opaque surrogate (tooltip explains the absent dim_code). */
function shortKey(codeKey: string): string {
  return codeKey.length > 8 ? `…${codeKey.slice(-6)}` : codeKey;
}

/** A subtle, clearly-labelled ADVISORY out-of-range marker (P2). The badge text always reads "advisory"
 *  and the tooltip carries the specific range + the "advisory, not diagnostic" disclaimer, so it can never
 *  be misread as a clinical/diagnostic determination. Rendered only when vitalAdvisory confidently flags. */
function AdvisoryMark({ reason }: { reason: string }) {
  return (
    <SkyTooltip content={`${reason}. ${VITAL_ADVISORY_LEGEND}`}>
      <SkyBadge color="warning" appearance="tint" size="small">
        Advisory
      </SkyBadge>
    </SkyTooltip>
  );
}

const columns: SkyDataGridColumn<Vital>[] = [
  {
    // Vital NAME leads — the REAL LOINC display (gold.dim_code) when resolved; the inferred type is the
    // fallback only. The opaque surrogate is shown ONLY when neither a name nor a type could be resolved.
    key: "code_key",
    header: "Vital",
    width: "22%",
    renderCell: (_v, row) => {
      const name = row.code_display?.trim();
      if (name) {
        return (
          <div className="flex flex-col">
            <span>{name}</span>
            {row.loinc_code && (
              // Use the DS type scale (SkyText size 100) instead of an inline fontSize that bypasses it.
              <SkyText size={100} color="muted">
                {row.code_system ?? "LOINC"} {row.loinc_code}
              </SkyText>
            )}
          </div>
        );
      }
      const info = deriveVitalType(row);
      if (info.kind === "coded") {
        return (
          <SkyTooltip content="Vital name not yet resolved (no dim_code match)">
            <span>
              {info.label} <span className="text-neutral-500">#{shortKey(row.code_key)}</span>
            </span>
          </SkyTooltip>
        );
      }
      return info.label;
    },
  },
  { key: "effective_date", header: "Date", width: "14%" },
  {
    key: "value_quantity",
    header: "Value",
    width: "18%",
    align: "right",
    renderCell: (_v, row) => {
      const info = deriveVitalType(row);
      if (info.kind === "unstructured") return row.value_string?.trim() || "Not recorded";
      if (row.value_quantity == null) return "";
      // ADVISORY (non-diagnostic) out-of-range marker — only when the type is confidently known.
      const advisory = vitalAdvisory(row);
      return (
        <span className="inline-flex items-center justify-end gap-1">
          {String(row.value_quantity)}
          {advisory && <AdvisoryMark reason={advisory} />}
        </span>
      );
    },
  },
  { key: "value_unit", header: "Unit", width: "10%" },
  {
    key: "component_systolic",
    header: "BP",
    width: "12%",
    renderCell: (_v, row) => {
      if (row.component_systolic == null || row.component_diastolic == null) return "";
      const advisory = vitalAdvisory(row);
      return (
        <span className="inline-flex items-center gap-1">
          {`${row.component_systolic}/${row.component_diastolic}`}
          {advisory && <AdvisoryMark reason={advisory} />}
        </span>
      );
    },
  },
  {
    key: "is_coded_flag",
    header: "Coded",
    width: "10%",
    renderCell: (_v, row) => (row.is_coded_flag ? "Yes" : "No"),
  },
  {
    key: "data_status",
    header: "Provenance",
    width: "16%",
    renderCell: (_v, row) => <ProvenanceBadge meta={rowProvenance(row)} />,
  },
];

/** Latest-value tile — one per distinct vital type (grouped by code_key). Titled by the REAL gold name. */
function LatestTile({ group }: { group: VitalGroup }) {
  const { info, latest, codeKey, displayName, hasGoldName } = group;
  // ADVISORY (non-diagnostic) out-of-range flag on the latest value — only when the type is confidently
  // known (deriveVitalType); free-text/ambiguous/percentage values are never flagged.
  const advisory = vitalAdvisory(latest);
  return (
    <SkyCard
      appearance="outline"
      header={{
        title: displayName,
        action: <ProvenanceBadge meta={rowProvenance(latest)} />,
      }}
    >
      <div className="flex flex-col gap-1">
        <SkyText size={500} weight="semibold" font="numeric">
          {formatLatest(latest, info)}
        </SkyText>
        {advisory && (
          <div className="flex items-center gap-1">
            <SkyBadge color="warning" appearance="tint" size="small">
              {VITAL_ADVISORY_LABEL}
            </SkyBadge>
            <SkyTooltip content={VITAL_ADVISORY_LEGEND}>
              <SkyText size={100} color="muted">
                {advisory}
              </SkyText>
            </SkyTooltip>
          </div>
        )}
        <SkyText size={200} color="muted">
          {latest.effective_date ?? "Date not recorded"}
        </SkyText>
        {hasGoldName && latest.loinc_code && (
          <SkyText size={200} color="muted">
            {latest.code_system ?? "LOINC"} {latest.loinc_code}
          </SkyText>
        )}
        {info.kind === "unstructured" && (
          <SkyText size={200} color="muted">
            Free-text, not a coded vital
          </SkyText>
        )}
        {!hasGoldName && info.kind === "coded" && (
          <SkyText size={200} color="muted">
            Vital name not resolved (no dim_code) · #{shortKey(codeKey)}
          </SkyText>
        )}
      </div>
    </SkyCard>
  );
}

export function Vitals() {
  // The selected member: a manual pick wins; else a ?member= deep-link (from Member 360 "view full"
  // links); else this section's exemplar (the vitals-richest REAL member) so the screen never defaults
  // to an empty member. The ParticipantPicker still lets the user change member.
  const [picked, setPicked] = useState<string | null>(null);
  const [params] = useSearchParams();
  const memberParam = params.get("member");
  const validParam = memberParam && /^-?\d+$/.test(memberParam) ? memberParam : null;
  const { key: defaultKey, error: exemplarError } = useExemplarMember("vitals");
  const key = picked ?? validParam ?? defaultKey;

  // pageSize=100 pulls enough history per member to build per-type tiles + trends in one fetch.
  const { data, meta, loading, error } = usePage<Vital[]>(
    key ? `/api/v1/participants/${key}/vitals?pageSize=100` : null,
    vitalListSchema,
  );

  // Array.isArray, not `?? []`: a drifted/non-array envelope is truthy, so `?? []` would pass it to
  // groupVitals whose `for..of` then throws "not iterable" during render. Guard the shape here.
  const groups = useMemo(() => groupVitals(Array.isArray(data) ? data : []), [data]);
  const measured = groups.filter((g) => g.info.kind !== "unstructured");
  const unstructured = groups.filter((g) => g.info.kind === "unstructured");
  const trends = measured
    .map((g) => ({ group: g, series: buildSeries(g) }))
    .filter((t): t is { group: VitalGroup; series: SparkSeries[] } => t.series !== null);

  const hasData = (data?.length ?? 0) > 0;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "vitals", label: "Vitals" }]}
        title="Vitals"
        description="Member vital signs read live from op_fact_observation. Each vital is named by its real LOINC display, resolved via gold.dim_code; where a code does not resolve the type falls back to the real columns (never fabricated). Free-text/unstructured rows are listed honestly and never trended."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <div className="flex items-end justify-between gap-4">
          <div className="w-80">
            <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
          </div>
          {key && <ProvenanceBadge meta={meta?.provenance} />}
        </div>

        {/* The default-member lookup failed and the user has not picked one yet: surface an honest bar
            (the picker above is the way out) instead of a silent blank screen. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}

        {loading && <SkyLoader label="Loading vitals" />}
        {error && <SkyMessageBar intent="error" title="Could not load vitals" message={error} />}

        {key && !loading && !error && !hasData && (
          <SkyDataGrid<Vital>
            data={[]}
            columns={columns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="Awaiting feed"
          />
        )}

        {key && !loading && !error && hasData && (
          <>
            {measured.length > 0 && (
              <div className="flex flex-col gap-2">
                <SkyText size={400} weight="semibold">
                  Latest values
                </SkyText>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {measured.map((g) => (
                    <LatestTile key={g.codeKey} group={g} />
                  ))}
                </div>
              </div>
            )}

            {trends.length > 0 && (
              <div className="flex flex-col gap-2">
                <SkyText size={400} weight="semibold">
                  Trends
                </SkyText>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {trends.map(({ group, series }) => (
                    <SkyCard key={group.codeKey} appearance="outline" header={{ title: group.displayName }}>
                      <VitalSparkline series={series} unit={group.info.kind === "bp" ? "mmHg" : group.latest.value_unit} />
                    </SkyCard>
                  ))}
                </div>
              </div>
            )}

            {unstructured.length > 0 && (
              <SkyCard
                appearance="outline"
                header={{
                  title: "Unstructured / free-text (DEFERRED until coded)",
                  description: "Free-text observations are listed honestly and never trended; they are not coded vitals.",
                  action: <ProvenanceBadge meta={rowProvenance(unstructured[0].latest)} />,
                }}
              >
                <ul className="flex flex-col gap-2">
                  {unstructured.flatMap((g) =>
                    g.rows.map((r, i) => (
                      <li key={`${g.codeKey}-${i}`} className="flex flex-col">
                        <SkyText size={300}>{r.value_string?.trim() || "Free-text (no value)"}</SkyText>
                        <SkyText size={200} color="muted">
                          {r.effective_date ?? "Date not recorded"}
                        </SkyText>
                      </li>
                    )),
                  )}
                </ul>
              </SkyCard>
            )}

            <div className="flex flex-col gap-2">
              <SkyText size={400} weight="semibold">
                All observations
              </SkyText>
              <SkyDataGrid<Vital>
                data={data ?? []}
                columns={columns}
                getRowId={(_r, i) => String(i)}
                emptyMessage="Awaiting feed"
              />
            </div>
          </>
        )}
      </section>
      <PageFooter />
    </>
  );
}
